'use client';

import React, { useEffect, useState } from 'react';
import PageHeader from '../../components/shared/PageHeader';
import PageFooter from '../../components/shared/PageFooter';

const SECTIONS = [
  { id: 'standards', title: 'Our Standards' },
  { id: 'reporting', title: 'Reporting' },
  { id: 'enforcement', title: 'Enforcement' },
  { id: 'contact', title: 'Contact' },
];

const CSAEContent = ({ embedded = false }: { embedded?: boolean }) => {
  const font = '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", sans-serif';
  const [activeSection, setActiveSection] = useState(SECTIONS[0].id);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        }
      },
      { rootMargin: '-20% 0px -60% 0px', threshold: 0 }
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const h2Style: React.CSSProperties = {
    fontSize: '18px',
    fontWeight: 700,
    color: 'white',
    marginBottom: '16px',
    paddingTop: '8px',
  };

  const pStyle: React.CSSProperties = {
    fontSize: '15px',
    lineHeight: 1.85,
    color: 'rgba(255,255,255,0.65)',
    margin: '0 0 16px',
  };

  const ulStyle: React.CSSProperties = {
    paddingLeft: '20px',
    lineHeight: 1.85,
    fontSize: '15px',
    color: 'rgba(255,255,255,0.65)',
    margin: '0 0 16px',
  };

  return (
    <>
      <style>{`
        .csae-layout {
          max-width: 680px;
          margin: 0 auto;
          padding: 0 24px;
        }
        .csae-sidebar {
          display: none;
        }
        .csae-content {
          width: 100%;
        }
        .csae-section {
          margin-bottom: 20px;
          padding-bottom: 20px;
          border-bottom: 1px solid rgba(255,255,255,0.06);
        }
        .csae-section:last-child {
          border-bottom: none;
        }

        .csae-sidebar-link {
          display: block;
          padding: 10px 16px;
          font-size: 14px;
          font-weight: 500;
          color: rgba(255,255,255,0.4);
          text-decoration: none;
          border-left: 2px solid transparent;
          transition: color 0.2s ease, border-color 0.2s ease;
          line-height: 1.4;
        }
        .csae-sidebar-link:hover {
          color: rgba(255,255,255,0.7);
        }
        .csae-sidebar-link.active {
          color: white;
          border-left-color: #a78bfa;
          font-weight: 600;
        }

        @media (min-width: 900px) {
          .csae-layout {
            max-width: 1060px !important;
            padding: 0 48px !important;
            display: flex !important;
            gap: 48px !important;
            align-items: flex-start !important;
          }
          .csae-sidebar {
            display: block !important;
            position: sticky !important;
            top: 32px !important;
            width: 240px !important;
            min-width: 240px !important;
            padding-top: 8px !important;
          }
          .csae-content {
            flex: 1 !important;
            min-width: 0 !important;
          }
          .csae-hero-title {
            font-size: 40px !important;
            text-align: left !important;
          }
          .csae-hero-subtitle {
            text-align: left !important;
            font-size: 19px !important;
          }
          .csae-hero-intro {
            text-align: left !important;
          }
        }

        /* Embedded mode (in-app modal) */
        .csae-embedded .csae-layout {
          flex-direction: column !important;
          padding: 0 20px !important;
        }
        .csae-embedded .csae-sidebar {
          display: none !important;
        }
        .csae-embedded .csae-hero-title {
          font-size: 28px !important;
          text-align: center !important;
          margin-top: 8px !important;
        }
        .csae-embedded .csae-hero-subtitle {
          text-align: center !important;
        }
        .csae-embedded .csae-hero-intro {
          text-align: center !important;
        }
        .csae-embedded .csae-content {
          padding-bottom: 120px !important;
        }
      `}</style>
      <div
        className={embedded ? 'csae-embedded' : ''}
        style={{
          background:
            'linear-gradient(180deg, #0a0f32 0%, #0a1230 8%, #0c1a35 18%, #0e2240 30%, #102a48 45%, #0e2240 60%, #0c1a35 75%, #0a1230 88%, #0a0f32 100%)',
          minHeight: embedded ? 'auto' : '100vh',
          color: '#fff',
          fontFamily: font,
        }}
      >
        {!embedded && <PageHeader />}

        {/* Hero */}
        <div style={{ maxWidth: '1060px', margin: '0 auto', padding: '0 48px' }}>
          <h1
            className="csae-hero-title"
            style={{
              fontSize: '32px',
              fontWeight: 800,
              marginTop: '40px',
              marginBottom: '8px',
              textAlign: 'center',
              fontFamily:
                '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif',
              color: 'white',
            }}
          >
            Child Safety Policy
          </h1>
          <p
            className="csae-hero-subtitle"
            style={{
              textAlign: 'center',
              fontSize: '17px',
              lineHeight: 1.7,
              fontWeight: 500,
              marginBottom: '12px',
              color: 'white',
            }}
          >
            <strong style={{ fontWeight: 800 }}>Last Updated:</strong> February 26, 2026
          </p>
          <p
            className="csae-hero-intro"
            style={{
              textAlign: 'center',
              fontSize: '15px',
              color: 'rgba(255,255,255,0.55)',
              marginBottom: '24px',
              lineHeight: 1.8,
              maxWidth: '680px',
            }}
          >
            Tabster is committed to providing a safe environment for all users and has a zero-tolerance policy toward child sexual abuse and exploitation (CSAE).
          </p>
        </div>

        {/* Main layout: sidebar + content */}
        <div className="csae-layout">
          {/* Sidebar nav */}
          <nav className="csae-sidebar">
            {SECTIONS.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className={`csae-sidebar-link ${activeSection === s.id ? 'active' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              >
                {s.title}
              </a>
            ))}
          </nav>

          {/* Content */}
          <div className="csae-content">
            <div id="standards" className="csae-section">
              <h2 style={h2Style}>Our Standards</h2>
              <ul style={ulStyle}>
                <li>Tabster strictly prohibits any content or behavior that sexually exploits or endangers minors.</li>
                <li>Any content that depicts, promotes, or facilitates child sexual abuse or exploitation is strictly forbidden on our platform.</li>
                <li>Users found sharing or distributing such content will be immediately banned and reported to the appropriate authorities, including the National Center for Missing and Exploited Children (NCMEC).</li>
              </ul>
            </div>

            <div id="reporting" className="csae-section">
              <h2 style={h2Style}>Reporting</h2>
              <p style={pStyle}>
                If you encounter any content on Tabster that you believe violates this policy, please report it immediately by contacting us at:{' '}
                <a
                  href="mailto:support@tabster.ai"
                  style={{ color: '#a78bfa', textDecoration: 'none', fontWeight: 600 }}
                >
                  support@tabster.ai
                </a>
                .
              </p>
              <p style={{ ...pStyle, margin: 0 }}>We take all reports seriously and will investigate promptly.</p>
            </div>

            <div id="enforcement" className="csae-section">
              <h2 style={h2Style}>Enforcement</h2>
              <p style={{ ...pStyle, margin: 0 }}>
                Violations of this policy will result in immediate account termination and, where appropriate, reporting to law enforcement and relevant authorities.
              </p>
            </div>

            <div id="contact" className="csae-section">
              <h2 style={h2Style}>Contact</h2>
              <p style={{ ...pStyle, margin: 0 }}>
                For questions about this policy, contact us at:{' '}
                <a
                  href="mailto:support@tabster.ai"
                  style={{ color: '#a78bfa', textDecoration: 'none', fontWeight: 600 }}
                >
                  support@tabster.ai
                </a>
                .
              </p>
            </div>
          </div>
        </div>

        {!embedded && <PageFooter />}
      </div>
    </>
  );
};

export default CSAEContent;
